import { useEffect, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { employeeService } from "../services/employeeService";

const SETTINGS_DOC = doc(db, "peakGlobalSettings", "config");

// Read by aiProxy in PeakPWA functions. Defaults and maximum must match
// DEFAULT_MONTHLY_TOKEN_ALLOWANCE and MAX_MONTHLY_TOKEN_ALLOWANCE in functions/src/aiUsage.ts.
const AI_ALLOWANCES_DOC = doc(db, "peakGlobalSettings", "aiAllowances");
const DEFAULT_AI_ALLOWANCES = { ams: 1_000_000, athlete: 250_000 };
const MAX_AI_ALLOWANCE = 100_000_000;
type AiAllowanceKey = keyof typeof DEFAULT_AI_ALLOWANCES;

const AI_ALLOWANCE_FIELDS: { key: AiAllowanceKey; label: string; help: string }[] = [
    {
        key: "ams",
        label: "Coaches (AMS)",
        help: "Monthly tokens per coach for AI in the AMS.",
    },
    {
        key: "athlete",
        label: "Athletes (PWA, iOS and Android)",
        help: "Monthly tokens per athlete, shared across the athlete apps.",
    },
];

const parseAllowance = (value: string): number | null => {
    const n = Number(value.replace(/[,\s]/g, ""));
    return value.trim() !== "" && Number.isInteger(n) && n >= 0 && n <= MAX_AI_ALLOWANCE ? n : null;
};

const AiAllowancesSettings: React.FC = () => {
    const { currentUser, userData } = useAuth();
    const canEdit = userData?.superAdmin === true;
    const [values, setValues] = useState<Record<AiAllowanceKey, string>>({ ams: "", athlete: "" });
    const [isDefault, setIsDefault] = useState(false);
    const [lastChange, setLastChange] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const loadAllowances = async () => {
        try {
            setLoading(true);
            setError(null);
            const snap = await getDoc(AI_ALLOWANCES_DOC);
            const data = snap.exists() ? snap.data() : {};
            const current = (key: AiAllowanceKey) =>
                typeof data[key] === "number" ? data[key] : DEFAULT_AI_ALLOWANCES[key];
            setValues({ ams: String(current("ams")), athlete: String(current("athlete")) });
            setIsDefault(!snap.exists());
            if (snap.exists() && typeof data.updatedBy === "string") {
                const editor = await employeeService.getEmployee(data.updatedBy).catch(() => null);
                const who = editor ? `${editor.firstName} ${editor.surname}`.trim() || editor.email : "an admin";
                const when = data.updatedAt?.toDate?.().toLocaleString("en-AU");
                setLastChange(`Last changed by ${who}${when ? ` on ${when}` : ""}.`);
            } else {
                setLastChange(null);
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Unknown error";
            console.error("Error loading AI allowances:", err);
            setError(`Failed to load AI allowances: ${message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAllowances();
    }, []);

    const handleSave = async () => {
        const ams = parseAllowance(values.ams);
        const athlete = parseAllowance(values.athlete);
        if (ams === null || athlete === null || !currentUser) {
            setError(`Enter whole numbers from 0 to ${MAX_AI_ALLOWANCE.toLocaleString("en-AU")}.`);
            setSuccess(null);
            return;
        }
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            await setDoc(AI_ALLOWANCES_DOC, {
                ams,
                athlete,
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid,
            });
            await loadAllowances();
            setSuccess("AI allowances saved. They apply from the next AI request.");
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Unknown error";
            console.error("Error saving AI allowances:", err);
            setError(`Failed to save AI allowances: ${message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border p-6 max-w-xl space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-heading">AI allowances</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Monthly AI token limits, reset on the 1st of each month (UTC). Changes apply
                        from the next AI request. Set a limit to 0 to turn AI off for that group.
                    </p>
                </div>
                {canEdit && (
                    <button
                        onClick={handleSave}
                        className="btn btn-primary btn-sm !h-10"
                        disabled={loading || saving}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                )}
            </div>

            {!canEdit && (
                <p className="text-xs text-gray-500">Only super admins can change AI allowances.</p>
            )}
            {error && (
                <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg">
                    {error}
                </div>
            )}
            {success && (
                <div className="p-3 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg">
                    {success}
                </div>
            )}

            {loading ? (
                <div className="text-sm text-gray-400">Loading AI allowances...</div>
            ) : (
                <>
                    {AI_ALLOWANCE_FIELDS.map(({ key, label, help }) => (
                        <div key={key}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:bg-gray-50 disabled:text-gray-500"
                                value={values[key]}
                                disabled={!canEdit}
                                onChange={(e) => {
                                    setValues((prev) => ({ ...prev, [key]: e.target.value }));
                                    setSuccess(null);
                                }}
                            />
                            <p className="text-xs text-gray-400 mt-1">
                                {help} Default {DEFAULT_AI_ALLOWANCES[key].toLocaleString("en-AU")}.
                            </p>
                        </div>
                    ))}
                    <p className="text-xs text-gray-400">
                        {isDefault ? "Using the default limits." : lastChange}
                    </p>
                </>
            )}
        </div>
    );
};

const PeakGlobalSettingsPage: React.FC = () => {
    const [androidMinVersion, setAndroidMinVersion] = useState("");
    const [iosMinVersion, setIosMinVersion] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const loadSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const snap = await getDoc(SETTINGS_DOC);
            if (snap.exists()) {
                const data = snap.data();
                setAndroidMinVersion(
                    typeof data.androidMinVersion === "string" ? data.androidMinVersion : ""
                );
                setIosMinVersion(
                    typeof data.iosMinVersion === "string" ? data.iosMinVersion : ""
                );
            } else {
                setAndroidMinVersion("");
                setIosMinVersion("");
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Unknown error";
            console.error("Error loading peak global settings:", err);
            setError(`Failed to load settings: ${message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            await setDoc(
                SETTINGS_DOC,
                {
                    androidMinVersion: androidMinVersion.trim(),
                    iosMinVersion: iosMinVersion.trim(),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            setSuccess("Settings saved.");
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Unknown error";
            console.error("Error saving peak global settings:", err);
            setError(`Failed to save settings: ${message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-6 p-6 h-[calc(100vh-64px)] overflow-y-auto">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading">Peak Global Settings</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Minimum app versions required to use Peak on native platforms.
                            Users below these versions will be asked to update.
                        </p>
                    </div>
                    <button
                        onClick={handleSave}
                        className="btn btn-primary btn-sm !h-10"
                        disabled={loading || saving}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>

                {error && (
                    <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="p-3 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg">
                        {success}
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center h-32 text-gray-400">
                        Loading settings...
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border p-6 max-w-xl space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Android Min Version
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. 0.1.0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                value={androidMinVersion}
                                onChange={(e) => {
                                    setAndroidMinVersion(e.target.value);
                                    setSuccess(null);
                                }}
                            />
                            <p className="text-xs text-gray-400 mt-1">
                                Compared against the Android app&apos;s versionName (semver-style).
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                iOS Min Version
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. 1.1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                value={iosMinVersion}
                                onChange={(e) => {
                                    setIosMinVersion(e.target.value);
                                    setSuccess(null);
                                }}
                            />
                            <p className="text-xs text-gray-400 mt-1">
                                Compared against the iOS app&apos;s marketing version (CFBundleShortVersionString).
                            </p>
                        </div>
                    </div>
                )}

                <AiAllowancesSettings />
            </div>
        </DashboardWrapper>
    );
};

export default PeakGlobalSettingsPage;
